import { useContext, useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useTranslation } from "react-i18next";
import { AbsoluteDiv, CustomDiv, InlineDiv } from "../divs";
import { AlmostBoldText, SemiBoldText } from "../texts";
import starIcon from "@/assets/images/icons/starIcon.svg";
import { FavoriteButton } from "@/components/activity/favoriteButton";
import googleIcon from "@/components/city-landing/images/google_icon.svg";
import tripAdvisorIcon from "@/components/city-landing/images/tripadvisor_logo.svg";
import GoogleTranslationComponent from "@/components/googleTranslate/GoogleTranslationComponent";
import FirebaseContext from "@/context/firebaseContext/firebaseContext";
import { ActivityCardProps, FirebaseContextType } from "@/types/components";
import { SECONDARY_BUTTON_COLOR } from "@/utils/constants";

const ActivityCard: React.FC<ActivityCardProps> = ({
  imageUrl: baseImageUrl,
  title,
  width,
  height,
  price,
  id,
  imInMapView,
  location,
  isFavorite,
  inFavoriteList = false,
  activity,
  trackEvent,
  TrackCustomEvents,
}) => {
  const { t } = useTranslation();
  const session = useSession();
  const [wasRemoved, setWasRemoved] = useState(false);
  const isMobile = useMediaQuery("(max-width:450px)");
  const { logAnalyticsEvent } = useContext(
    FirebaseContext,
  ) as FirebaseContextType;

  const imageUrl = useMemo(() => {
    if (!baseImageUrl.includes("cdn.filestackcontent.com")) {
      return (
        baseImageUrl +
        `?height=${isMobile ? "254" : "291"}&format=webp&quality=100`
      );
    }
    return baseImageUrl.replace(
      "https://cdn.filestackcontent.com/",
      `https://cdn.filestackcontent.com/resize=height:${isMobile ? "254" : "291"}/`,
    );
  }, [baseImageUrl, isMobile]);

  if (wasRemoved && inFavoriteList) {
    return null;
  }

  const getSlug = () => {
    if (activity.slugEs) {
      return activity.slugEs;
    } else {
      return id;
    }
  };
  return (
    <CustomDiv
      ref={location?.ref}
      width={
        isMobile ? "220px"
        : width ?
          width
        : imInMapView ?
          "47%"
        : "291px"
      }
      height={
        isMobile ? "220px"
        : height ?
          height
        : imInMapView ?
          "47%"
        : "291px"
      }
      style={{
        backgroundImage:
          "linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.9)), url(" +
          imageUrl +
          ")",
        backgroundSize: "cover",
        borderRadius: "12px",
        minWidth:
          isMobile ? "220px"
          : width ? width
          : imInMapView ? "200px"
          : "291px",
        maxWidth: isMobile ? "220px" : "291px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <InlineDiv
        style={{
          position: "absolute",
          justifyContent: "space-between",
          maxWidth: isMobile ? "100px" : "300px",
          marginTop: isMobile ? "15px" : "15px",
          marginLeft: "-10px",
        }}
      >
        <InlineDiv marginX="10px">
          {activity.fh_tripadvisor_rating ?
            activity.fh_tripadvisor_rating > 0 ?
              <InlineDiv
                width="79px"
                style={{
                  justifyContent: "space-evenly",
                  background: "white",
                  marginLeft: "10px",
                  borderRadius: "16px",
                  alignItems: "center",
                }}
              >
                <Image
                  style={{ marginTop: "1px" }}
                  src={tripAdvisorIcon}
                  alt="tripAdvisor"
                  width={22}
                  height={22}
                />
                <AlmostBoldText style={{ fontWeight: "400", color: "black" }}>
                  {activity.fh_tripadvisor_rating}
                </AlmostBoldText>
                <Image
                  src={starIcon}
                  alt="star"
                  width={22}
                  height={22}
                  style={{ marginTop: "1px" }}
                />
              </InlineDiv>
            : null
          : null}
          {activity.fh_google_rating ?
            activity.fh_google_rating > 0 ?
              <InlineDiv
                width="70px"
                style={{
                  justifyContent: "space-evenly",
                  background: "white",
                  marginLeft: "10px",
                  borderRadius: "16px",
                  alignItems: "center",
                }}
              >
                <Image
                  src={googleIcon}
                  alt="google"
                  width={22}
                  height={22}
                  style={{
                    maxHeight: "22px",
                    maxWidth: "22px",
                    marginTop: "0.5px",
                  }}
                />
                <AlmostBoldText
                  style={{
                    fontWeight: "400",
                    color: "black",
                    lineHeight: "0px",
                  }}
                >
                  {activity.fh_google_rating}
                </AlmostBoldText>
                <Image
                  src={starIcon}
                  alt="star"
                  width={22}
                  height={22}
                  style={{ marginTop: "1px" }}
                />
              </InlineDiv>
            : null
          : null}
        </InlineDiv>
      </InlineDiv>
      {session.data?.user && (
        <FavoriteButton
          id={Number(id)}
          sx={{
            width: "32px",
            height: "32px",
            position: "absolute",
            right: "12px",
            top: "12px",
          }}
          isFavoriteActivity={isFavorite}
          setFlagRemoved={setWasRemoved}
          activity={activity}
        ></FavoriteButton>
      )}
      <Link
        href={"/activity/" + getSlug()}
        style={{ display: "grid", height: "auto" }}
        target={"_blank"}
        onClick={() => {
          trackEvent && TrackCustomEvents ?
            trackEvent({
              name: TrackCustomEvents.EVENT_BROWSED,
              properties: {
                activity_id: activity.id,
                activity_name: activity.name,
                activity_category: activity.category,
                activity_subcategory: activity.subcategory,
                vendor_id: activity.supplier?.id,
                vendor_name: activity.supplier?.companyName,
              },
            })
          : null;
          TrackCustomEvents ?
            logAnalyticsEvent(TrackCustomEvents.EVENT_BROWSED_WEB, {
              activity_id: activity.id,
              activity_name: activity.name,
              activity_category: activity.category,
              activity_subcategory: activity.subcategory,
              vendor_id: activity.supplier?.id,
              vendor_name: activity.supplier?.companyName,
            })
          : null;
        }}
      >
        <Box
          sx={{
            marginTop:
              imInMapView ? "50%"
              : isMobile ? "135px"
              : "202px",
            marginLeft: "16px",
            marginRight: "16px",
            marginBottom: "16px",
          }}
        >
          <Box
            sx={{
              height: "40px",
              maxHeight: "40px",
              marginTop: "8px",
              overflow: "hidden",
            }}
          >
            <SemiBoldText
              className="overflowOnTwoLines"
              fontSize="14px"
              color={SECONDARY_BUTTON_COLOR}
            >
              <GoogleTranslationComponent text={title} />
            </SemiBoldText>
          </Box>
          <InlineDiv style={{ marginTop: "0px" }}>
            <SemiBoldText fontSize="20px" color={SECONDARY_BUTTON_COLOR}>
              {t("activity.from") + " " + price}
            </SemiBoldText>
            <AbsoluteDiv right="0">
              <InlineDiv style={{ marginTop: "5px" }}>
                {activity.manualDuration ?
                  <SemiBoldText color="#FFFFFF">
                    {activity.manualDuration}
                  </SemiBoldText>
                : null}
              </InlineDiv>
            </AbsoluteDiv>
          </InlineDiv>
        </Box>
      </Link>
    </CustomDiv>
  );
};

export default ActivityCard;
